import React from 'react';
import styled from 'styled-components';
import { Mono } from '../../../../../../../generic/ui/Mono';
import UserIcon from '../../../../../../../generic/icons/IconLib/UserIcon';
import BarcodeIcon from '../../../../../../../generic/icons/IconLib/BarcodeIcon';
import ScaleIcon from '../../../../../../../generic/icons/IconLib/ScaleIcon';
import BoxesIcon from '../../../../../../../generic/icons/IconLib/BoxesIcon';
import PackageBoxIcon from '../../../../../../../generic/icons/IconLib/PackageBoxIcon';

interface Props {
    title: string;
    growerName?: string;
    goodsCode?: string;
    packName?: string;
    packType?: string;
    imageBg: string;
}

const LikutEntryHeaderCard: React.FC<Props> = ({
    title,
    growerName,
    goodsCode,
    packName,
    packType,
    imageBg,
}) => {
    const packIcon = packType === '1'
        ? <ScaleIcon color='currentColor' size={14} />
        : packType === '2'
            ? <BoxesIcon color='currentColor' size={14} />
            : <PackageBoxIcon color='currentColor' size={14} />;

    return (
        <HeaderCard>
            <InfoColumn>
                <TitleText>{title}</TitleText>
                {growerName?.trim() ? (
                    <InfoLine>
                        <IconSlot>
                            <UserIcon color='currentColor' size={14} />
                        </IconSlot>
                        <InfoText>{growerName.trim()}</InfoText>
                    </InfoLine>
                ) : null}
                <MetaLine>
                    {goodsCode ? (
                        <InfoLine>
                            <IconSlot>
                                <BarcodeIcon color='currentColor' size={14} />
                            </IconSlot>
                            <MetaValue>{goodsCode}</MetaValue>
                        </InfoLine>
                    ) : null}
                    {packName?.trim() ? (
                        <InfoLine>
                            <IconSlot>{packIcon}</IconSlot>
                            <InfoText>{packName.trim()}</InfoText>
                        </InfoLine>
                    ) : null}
                </MetaLine>
            </InfoColumn>
            <ImageSlot style={{ background: imageBg, backgroundSize: 'contain' }} />
        </HeaderCard>
    );
};

export default LikutEntryHeaderCard;

const HeaderCard = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;
    direction: rtl;
    padding: 0.75rem 1rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.lg};
    box-shadow: ${({ theme }) => theme.shadows.card};
    flex-shrink: 0;
`;

const InfoColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const TitleText = styled.span`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.sans};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const InfoLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
`;

const MetaLine = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
`;

const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const InfoText = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const MetaValue = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;
`;

const ImageSlot = styled.div`
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.bg};
    flex-shrink: 0;
`;
